import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { decryptFilters } from "../utils/encryptUtils";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import "./viewSale.css";
import { useAuth } from "../context/authContext";
import { getSriLankaToday, getSriLankaDaysAgo } from "../utils/dateUtils";

function SharePage() {
  const { data } = useParams();
  const { token } = useAuth();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;

    const decoded = decryptFilters(decodeURIComponent(data));
    if (!decoded) {
      setError("Invalid or expired share link.");
      return;
    }

    let url = `${process.env.REACT_APP_BACKEND_URL}/api/get-sales?`;
    const {
      dateFilter,
      customStartDate,
      customEndDate,
      advanceFilterEnabled,
      agentName,
      customerType,
      bookingType,
      paymentMethod,
      showComments,
    } = decoded;

    const today = getSriLankaToday();

    if (dateFilter === "Today") url += `date=${today}`;
    else if (dateFilter === "This Week") {
      const start = getSriLankaDaysAgo(new Date().getDay());
      url += `startDate=${start}&endDate=${today}`;
    } else if (dateFilter === "This Month") {
      const start = getSriLankaDaysAgo(new Date().getDate() - 1);
      url += `startDate=${start}&endDate=${today}`;
    } else if (dateFilter === "Custom Date") {
      url += `date=${customStartDate}`;
    } else if (dateFilter === "Custom Range") {
      url += `startDate=${customStartDate}&endDate=${customEndDate}`;
    }

    if (advanceFilterEnabled) {
      if (agentName) url += `&agentName=${agentName}`;
      if (customerType) url += `&customerType=${customerType}`;
      if (bookingType) url += `&bookingType=${bookingType}`;
      if (paymentMethod) url += `&paymentMethod=${paymentMethod}`;
    }

    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const totalSales = res.data.reduce(
          (sum, s) => sum + s.numberOfTickets,
          0
        );
        const totalPrice = res.data.reduce((sum, s) => sum + s.totalPrice, 0);

        setSummary({
          totalSales,
          totalPrice,
          label:
            dateFilter === "Custom Date"
              ? `Date: ${customStartDate}`
              : dateFilter === "Custom Range"
              ? `From ${customStartDate} to ${customEndDate}`
              : `Date Filter: ${dateFilter}`,
          showComments,
        });

        setSales(res.data);
      })
      .catch((err) => {
        setError("Failed to load shared sales.");
      });
  }, [data, token]);

  const handleExportCSV = () => {
    if (sales.length === 0) return;

    const filtered = sales.map((s) => {
      const obj = {
        Agent: s.salesAgent,
        BookingType: s.bookingType,
        Tickets: s.numberOfTickets,
        TotalPrice: s.totalPrice,
        PaymentMethod: s.paymentMethod,
        CustomerType: s.customerType,
        Date: s.date,
        Time: s.time,
      };
      if (summary?.showComments) obj.Comments = s.comments || "";
      return obj;
    });

    const csvRows = [];
    const headers = Object.keys(filtered[0]);
    csvRows.push(headers.join(","));

    for (const row of filtered) {
      const values = headers.map((h) => `"${row[h]}"`);
      csvRows.push(values.join(","));
    }

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, "shared-sales.csv");
  };

  const handleExportXLSX = () => {
    const filtered = sales.map((s) => {
      const obj = {
        Agent: s.salesAgent,
        BookingType: s.bookingType,
        Tickets: s.numberOfTickets,
        TotalPrice: s.totalPrice,
        PaymentMethod: s.paymentMethod,
        CustomerType: s.customerType,
        Date: s.date,
        Time: s.time,
      };
      if (summary?.showComments) obj.Comments = s.comments || "";
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(filtered);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shared Sales");
    const xlsxBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([xlsxBuffer], { type: "application/octet-stream" });
    saveAs(blob, "shared-sales.xlsx");
  };

  if (error) return <div className="view-sale-container">{error}</div>;
  if (!summary)
    return <div className="view-sale-container">Loading shared sales...</div>;

  return (
    <div className="view-sale-container">
      <h1 className="view-sale-title">Shared Sales</h1>

      <div className="totals-bar">
        <div>
          <strong>{summary.label}</strong>
        </div>
        <div>Total Sales: {summary.totalSales}</div>
        <div>Total Price: LKR {summary.totalPrice.toLocaleString()}</div>
      </div>

      <div className="filters-bar export-buttons">
        <button onClick={handleExportCSV}>Export CSV</button>
        <button onClick={handleExportXLSX}>Export XLSX</button>
      </div>

      {sales.length > 0 ? (
        <div className="table-container">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Booking</th>
                <th>Tickets</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Time</th>
                {summary.showComments && <th>Comments</th>}
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr key={sale._id}>
                  <td>{sale.salesAgent}</td>
                  <td>{sale.bookingType}</td>
                  <td>{sale.numberOfTickets}</td>
                  <td>LKR {sale.totalPrice.toLocaleString()}</td>
                  <td>{sale.paymentMethod}</td>
                  <td>{sale.customerType}</td>
                  <td>{sale.date}</td>
                  <td>{sale.time}</td>
                  {summary.showComments && <td>{sale.comments || "-"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No data found.</div>
      )}
    </div>
  );
}

export default SharePage;
